/**
 * Endpoint paths for the subscriptions domain.
 *
 * Paths starting with `v1/` target `subscriptionsApi` (the new
 * parrot-subscriptions-api). Paths starting with `api/v1/` or
 * `v1/subscriptions/` target `parrotApi` (the legacy backend).
 *
 * All paths are relative — no leading slash — so axios appends them
 * to the baseURL instead of replacing it.
 */

/** Subscription block — parrot-subscriptions-api */
export const SUBSCRIPTION_BLOCK_ENDPOINTS = {
  BASE: 'v1/subscription-block/',
  BY_ID: (blockUuid: string) => `v1/subscription-block/${blockUuid}`,
  GENERATE_REFERENCE: (blockUuid: string) => `v1/subscription-block/${blockUuid}/generate-reference`,
  ATTACH_PAYMENT_METHOD: (blockUuid: string) => `v1/subscription-block/${blockUuid}/attach-payment-method`,
} as const;

/** Stores — parrot-subscriptions-api (consumed from the subscription block creation flow) */
export const STORE_ENDPOINTS = {
  /**
   * When `blockUuid` is provided (edit flow) the backend returns the block's
   * own stores plus the ungrouped ones, so the multiselect can prefill.
   */
  LIST: (blockUuid?: string) => (blockUuid ? `v1/stores/?block_uuid=${blockUuid}` : 'v1/stores/'),
} as const;

/** Invoice data — CRUD on subscriptions-api, fiscal regimes on legacy */
export const INVOICE_DATA_ENDPOINTS = {
  BASE: 'v1/invoice-data',
  BY_ID: (invoiceDataUuid: string) => `v1/invoice-data/${invoiceDataUuid}`,
  FISCAL_REGIMES: 'api/v1/invoicing/fiscal-regimes/',
  /**
   * Re-invoices a payment from scratch, synchronously: cancels the live CFDIs at the PAC, drops
   * their records and stamps the new ones inside the request. Scope goes in `?only_failed=`.
   */
  INVOICE_ACTION: (paymentUuid: string) => `v1/payments/${paymentUuid}/reinvoice-sync`,
} as const;

/** Constancia de situación fiscal — parrot-onboarding-api */
export const CSF_ENDPOINTS = {
  EXTRACT: 'v1/csf/extract',
} as const;

/** Payment history — subscriptions-api */
export const PAYMENT_HISTORY_ENDPOINTS = {
  LIST: 'v1/payments',
} as const;

/** Collection-status events — subscriptions-api */
export const SUBSCRIPTION_EVENTS_ENDPOINTS = {
  LIST: 'v1/events',
} as const;

/**
 * Payment method — parrot-subscriptions-api. Stripe session routes live under
 * the subscription-block resource; `LIST` is the org-level saved payment
 * methods listing (the org is derived server-side from the Bearer token).
 */
export const PAYMENT_METHOD_ENDPOINTS = {
  LIST: 'v1/payment-methods',
  GENERATE_SESSION: (blockUuid: string) => `v1/subscription-block/${blockUuid}/generate-session`,
  CONFIRM_SESSION: (blockUuid: string) => `v1/subscription-block/${blockUuid}/confirm-session`,
  FORCE_PAYMENT: (blockUuid: string) => `v1/subscription-block/${blockUuid}/force-payment`,
} as const;
